import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { calcProfit, getProductPrice } from '../../utils/pricing';

export default function DashboardCards() {
    const { products, sales, expenses, loadingData } = useAppContext();

    // Calcular métricas - deve estar antes do early return
    const { revenue, totalExpenses, profit, marginPercent } = calcProfit(
        products,
        sales,
        expenses
    );

    // Calcular métricas adicionais
    const metrics = useMemo(() => {
        const totalSalesQty = sales.reduce((sum, s) => sum + s.quantity, 0);
        const avgTicket = sales.length > 0 ? revenue / sales.length : 0;

        // Vendas dos últimos 7 dias
        const today = new Date();
        const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const recentSales = sales.filter(s => new Date(s.date) >= last7Days);
        const recentRevenue = recentSales.reduce((sum, s) => {
            const product = products.find(p => p.id === s.productId);
            if (!product) return sum;
            return sum + (getProductPrice(product) * s.quantity);
        }, 0);

        // Despesas dos últimos 7 dias
        const recentExpenses = expenses.filter(e => new Date(e.date) >= last7Days);
        const recentExpensesTotal = recentExpenses.reduce((sum, e) => sum + e.amount, 0);

        return {
            totalSalesQty,
            avgTicket,
            recentRevenue,
            recentExpensesTotal,
            totalSales: sales.length,
            totalProducts: products.length
        };
    }, [products, sales, expenses, revenue]);

    // Early return após todos os hooks
    if (loadingData) {
        return (
            <div className="alert alert-info">
                Carregando dados do MicroGestor...
            </div>
        );
    }

    const format = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

    const cards = [
        {
            title: 'Receita Total',
            value: `R$ ${format(revenue)}`,
            color: 'primary',
            subtitle: `${metrics.totalSales} vendas`,
            icon: <i className="bi bi-cash-stack"></i>,
            link: '/vendas'
        },
        {
            title: 'Despesas Totais',
            value: `R$ ${format(totalExpenses)}`,
            color: 'danger',
            subtitle: `${expenses.length} lançamentos`,
            icon: <i className="bi bi-receipt"></i>,
            link: '/despesas'
        },
        {
            title: 'Lucro Líquido',
            value: `R$ ${format(profit)}`,
            color: profit >= 0 ? 'success' : 'warning',
            subtitle: `Margem: ${marginPercent.toFixed(1)}%`,
            icon: profit >= 0 ? <i className="bi bi-graph-up-arrow"></i> : <i className="bi bi-graph-down-arrow"></i>,
            link: '/relatorios'
        },
        {
            title: 'Ticket Médio',
            value: `R$ ${format(metrics.avgTicket)}`,
            color: 'info',
            subtitle: `${metrics.totalSalesQty} itens vendidos`,
            icon: <i className="bi bi-cart-check"></i>,
            link: '/vendas'
        },
        {
            title: 'Produtos Cadastrados',
            value: metrics.totalProducts,
            color: 'secondary',
            subtitle: 'estoque ativo',
            icon: <i className="bi bi-box-seam"></i>,
            link: '/produtos'
        },
        {
            title: 'Receita (7 dias)',
            value: `R$ ${format(metrics.recentRevenue)}`,
            color: 'primary',
            subtitle: 'última semana',
            icon: <i className="bi bi-calendar-week"></i>,
            link: '/vendas'
        },
        {
            title: 'Despesas (7 dias)',
            value: `R$ ${format(metrics.recentExpensesTotal)}`,
            color: 'danger',
            subtitle: 'última semana',
            icon: <i className="bi bi-calendar-week"></i>,
            link: '/despesas'
        },
        {
            title: 'Lucro (7 dias)',
            value: `R$ ${format(metrics.recentRevenue - metrics.recentExpensesTotal)}`,
            color: metrics.recentRevenue - metrics.recentExpensesTotal >= 0 ? 'success' : 'warning',
            subtitle: 'última semana',
            icon: <i className="bi bi-fire"></i>,
            link: '/relatorios'
        },
    ];

    return (
        <div className="row g-3 mb-4">
            {cards.map((c) => (
                <div className="col-6 col-lg-3" key={c.title}>
                    <Link to={c.link} style={{ textDecoration: 'none' }}>
                        <div className={`card text-bg-${c.color} h-100 shadow-sm`} style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onMouseEnter={(e) => {e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';}} onMouseLeave={(e) => {e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '';}}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="small opacity-75">{c.title}</div>
                                    <div className="fs-4">{c.icon}</div>
                                </div>
                                <div className="fs-5 fw-bold mb-1">{c.value}</div>
                                <div className="small opacity-75">{c.subtitle}</div>
                            </div>
                        </div>
                    </Link>
                </div>
            ))}
        </div>
    );
}
